<template>
  <div class="comparison" :class="{ 'comparison--single': !showReferencePane }">
    <!-- Live column -->
    <section class="pane pane--live">
      <header class="pane-header">
        <span class="pane-tag pane-tag--live">
          <span class="pulse" />
          LIVE
        </span>
        <span class="pane-caption">Streaming now</span>
      </header>
      <div class="pane-body pane-body--live" id="stream-modal-body">
        <VideoStream
          :serialNumber="serialNumber"
          :deviceSettings="deviceSettings"
          @closeModal="$emit('closeModal')"
        />
      </div>
    </section>

    <!-- Reference column -->
    <section v-if="showReferencePane" class="pane pane--reference">
      <header class="pane-header">
        <span class="pane-tag pane-tag--reference">
          <camera-outlined />
          REFERENCE
        </span>
        <span class="pane-caption">
          {{ referenceCaption }}
        </span>
      </header>
      <div class="pane-body pane-body--reference">
        <div v-if="loadingReference" class="reference-status">
          <a-spin />
          <div class="reference-status-text">Loading reference…</div>
        </div>

        <div v-else-if="referenceError" class="reference-status">
          <picture-outlined class="reference-status-icon" />
          <div class="reference-status-title">{{ referenceError }}</div>
          <div class="reference-status-sub">
            Configure a reference frame for this station from the device
            setup page.
          </div>
        </div>

        <img
          v-else-if="referenceUrl"
          :src="referenceUrl"
          alt="Reference frame"
          class="reference-image"
          @error="handleImageError"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { CameraOutlined, PictureOutlined } from '@ant-design/icons-vue';
import DeviceService from 'src/services/device';
import S3Service from 'src/services/s3';
import { mapGetters } from 'vuex';
import VideoStream from './VideoStream.vue';

export default {
  name: 'LiveStreamComparison',
  components: { VideoStream, CameraOutlined, PictureOutlined },
  props: {
    // Same props VideoStream needs
    serialNumber: { type: String, required: true },
    deviceSettings: { type: Object, required: true, default: () => ({}) },
    // Extra props to look up the reference frame
    deviceId: { type: [Number, String], default: null },
    taskId: { type: [Number, String], default: null },
  },
  emits: ['closeModal', 'referenceStatus'],
  data() {
    return {
      loadingReference: true,
      referenceError: '',
      referenceUrl: '',
      referencePath: '',
    };
  },
  computed: {
    ...mapGetters(['organization']),
    referenceCaption() {
      if (this.loadingReference) return 'Fetching layout…';
      if (this.referenceError) return 'Unavailable';
      return 'Recorded layout';
    },
    // Hide the reference pane entirely when it isn't available so the live
    // stream expands to full width. Keep it visible while loading so we
    // don't briefly collapse then re-expand.
    showReferencePane() {
      return this.loadingReference || !!this.referenceUrl;
    },
  },
  mounted() {
    this.loadReference();
  },
  methods: {
    async loadReference() {
      if (!this.deviceId || !this.taskId) {
        this.loadingReference = false;
        this.referenceError = 'No station-operation pairing selected.';
        this.$emit('referenceStatus', false);
        return;
      }
      this.loadingReference = true;
      this.referenceError = '';

      const [pairErr, pair] = await DeviceService.getDeviceTaskByDeviceAndTask(
        this.deviceId,
        this.taskId
      );

      if (pairErr) {
        const status = pairErr.response?.status;
        this.loadingReference = false;
        if (status === 404) {
          this.referenceError = 'No reference image configured for this station.';
        } else {
          this.referenceError = 'Reference image unavailable.';
        }
        this.$emit('referenceStatus', false);
        return;
      }

      const path = pair?.camera_reference_path;
      if (!path) {
        this.loadingReference = false;
        this.referenceError = 'No reference image configured for this station.';
        this.$emit('referenceStatus', false);
        return;
      }
      this.referencePath = path;

      // The S3 training bucket is named after the internal org identifier
      // (e.g. "stihlboqtaf38skyuhdv1b-training"), which is what the
      // pairing response returns as `Organization`. The Vuex `organization`
      // getter can be a short display slug and won't match the bucket.
      const orgForBucket = pair?.Organization || this.organization;
      const bucket = `${orgForBucket}-training`;
      const objectPath = `${path}/reference_frame_original.jpg`;
      const [urlErr, data] = await S3Service.generatePresignedUrl(
        bucket,
        objectPath,
        '',
        false
      );

      this.loadingReference = false;
      if (urlErr || !data?.presigned_url) {
        this.referenceError = 'Reference image unavailable.';
        this.$emit('referenceStatus', false);
        return;
      }
      this.referenceUrl = data.presigned_url;
      this.$emit('referenceStatus', true);
    },
    handleImageError() {
      this.referenceUrl = '';
      this.referenceError = 'Reference image failed to load.';
      this.$emit('referenceStatus', false);
    },
  },
};
</script>

<style scoped>
.comparison {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
}
.comparison--single {
  grid-template-columns: 1fr;
}

.pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.pane-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 4px 8px;
}
.pane-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
}
.pane-tag--live {
  background: #fff1f0;
  color: #cf1322;
}
.pane-tag--reference {
  background: #f5f5f5;
  color: #595959;
}
.pane-caption {
  font-size: 12px;
  color: #8c8c8c;
}

.pulse {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #f5222d;
  animation: pulse 1.6s ease-in-out infinite;
}
@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 34, 45, 0.55);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 34, 45, 0);
    transform: scale(1.05);
  }
}

.pane-body {
  background: #0f172a;
  border-radius: 10px;
  overflow: hidden;
  min-height: 440px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}
.pane-body--live :deep(video) {
  width: 100% !important;
  height: 100% !important;
  max-height: 520px;
  object-fit: contain;
  background: #000;
}
.pane-body--reference {
  background: #1f2937;
}
.reference-image {
  width: 100%;
  height: 100%;
  max-height: 520px;
  object-fit: contain;
  display: block;
}

.reference-status {
  color: #cbd5e1;
  text-align: center;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 340px;
}
.reference-status-icon {
  font-size: 32px;
  color: #64748b;
}
.reference-status-title {
  font-size: 14px;
  font-weight: 600;
}
.reference-status-sub {
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.5;
}

@media (max-width: 900px) {
  .comparison {
    grid-template-columns: 1fr;
  }
  .pane-body {
    min-height: 300px;
  }
}
</style>
